// A rehype plugin that turns known entity names inside lore markdown into
// internal links (see ../lore/glossary). It walks the HAST tree's text nodes
// and, for any name in the glossary, splices in an <a> element.
//
// - Skips text inside <a>, <code>, <pre>, and headings (no nested links, no
//   links inside code, and headings stay clean).
// - Plurals are tolerated (e.g. "Brumbles" -> the "Brumble" entry).
// - The produced <a> only carries href; the Markdown component adds the class
//   and the hover tip (looked up from the link's own text).

import { getGlossary, findTerm } from '../lore/glossary'

const SKIP_TAGS = new Set(['a', 'code', 'pre', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'])

// HAST nodes are typed loosely to avoid pulling in @types/hast.
type Node = any

function isElement(n: Node): boolean {
  return n && n.type === 'element'
}

/** Recursively process an element's children, splicing in links where due. */
function walk(el: Node): void {
  if (!el || !Array.isArray(el.children)) return
  const allowed = !SKIP_TAGS.has(el.tagName)
  const next: Node[] = []
  for (const child of el.children) {
    if (allowed && child.type === 'text') {
      next.push(...splitText(child.value))
    } else {
      next.push(child)
      if (isElement(child)) walk(child)
    }
  }
  el.children = next
}

/** Split a text string into a mix of text nodes and <a> link nodes. */
function splitText(value: string): Node[] {
  const g = getGlossary()
  const out: Node[] = []
  let last = 0
  g.regex.lastIndex = 0
  let m: RegExpExecArray | null
  while ((m = g.regex.exec(value)) !== null) {
    const matched = m[1]
    if (!matched) continue
    const start = m.index
    if (start > last) out.push({ type: 'text', value: value.slice(last, start) })
    const term = findTerm(matched)
    if (term) {
      out.push({
        type: 'element',
        tagName: 'a',
        properties: { href: term.href },
        children: [{ type: 'text', value: matched }],
      })
    } else {
      out.push({ type: 'text', value: matched })
    }
    last = start + matched.length
    if (m.index === g.regex.lastIndex) g.regex.lastIndex++ // guard against zero-length loops
  }
  if (last < value.length) out.push({ type: 'text', value: value.slice(last) })
  return out
}

/** rehype plugin entry point: returns a transformer that rewrites the tree. */
export function rehypeAutolink() {
  return (tree: Node) => {
    walk(tree)
  }
}
